/**
 * Created by Cascade on 22.01.2026
 * Points History Screen - Shows full history of earned points
 */
import * as React from 'react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';
import { useTrans } from '../../Store/System/LocalizationStore';
import { EarnModelHistory } from '../../Model/Engagement/Earn/EarnModelHistory';
import { LoadingContainer } from '../../Components/LoadingContainer';
import { Theme } from '../../Theme/Theme';
import { useEarnHistory } from '../../Hooks/useEarnHistory';
import { getMediaSmallest } from '../../Model/Media/Media';

dayjs.extend(isToday);
dayjs.extend(isYesterday);

/** Safely extract a display string from a value that may be a plain string,
 *  a {lang, value} object, or an array of {lang, value} objects. */
function toText(val: unknown, fallback = ''): string {
  if (typeof val === 'string') return val;
  if (!val) return fallback;
  if (Array.isArray(val)) {
    const match = val.find((v: any) => v?.value);
    return match?.value ?? fallback;
  }
  if (typeof val === 'object' && 'value' in (val as any)) {
    return (val as any).value ?? fallback;
  }
  return fallback;
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ScreenContainer = styled.div`
  background: #f8f9fa;
  min-height: 100vh;
  animation: ${fadeIn} 0.3s ease-out;
`;

const PageHeader = styled.div`
  background: white;
  padding: 20px 0;
  border-bottom: 1px solid ${Theme.Colors.lighterGrey};
  position: sticky;
  top: 60px;
  z-index: 10;
`;

const HeaderContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 16px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackButton = styled.button`
  background: ${Theme.Colors.lightestGrey};
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: ${Theme.Colors.lighterGrey};
  }
`;

const HeaderTitle = styled.h1`
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: ${Theme.Colors.textColor};
`;

const PageContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 24px 16px 48px;
`;

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HistoryItem = styled.div`
  background: white;
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 14px;
  animation: ${fadeIn} 0.3s ease-out;
`;

const HistoryThumbnail = styled.div<{ src?: string }>`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: ${(props) => (props.src ? `url(${props.src})` : Theme.Colors.lightestGrey)};
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
`;

const HistoryContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const HistoryTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const HistoryMeta = styled.div`
  font-size: 12px;
  color: ${Theme.Colors.warmGrey};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TypeBadge = styled.span`
  background: ${Theme.Colors.lightestGrey};
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
`;

const HistoryPoints = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${Theme.Colors.primary};
  white-space: nowrap;
`;

const DateHeader = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${Theme.Colors.warmGrey};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 24px 0 12px;

  &:first-of-type {
    margin-top: 0;
  }
`;

const LoadMoreButton = styled.button`
  width: 100%;
  padding: 14px;
  background: white;
  border: 1px solid ${Theme.Colors.lighterGrey};
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  color: ${Theme.Colors.primary};
  cursor: pointer;
  margin-top: 16px;
  transition: background 0.2s;

  &:hover {
    background: ${Theme.Colors.lightestGrey};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 24px;
  background: white;
  border-radius: 16px;
  color: ${Theme.Colors.warmGrey};
`;

const EmptyEmoji = styled.div`
  font-size: 48px;
  margin-bottom: 12px;
`;

const getIconForEventType = (eventType?: string): string => {
  switch (eventType) {
    case 'activityWorkout':
      return '💪';
    case 'meditation':
      return '🧘';
    case 'article':
      return '📖';
    case 'recipe':
      return '🥗';
    case 'challenge':
      return '🏆';
    case 'webinar':
      return '🎓';
    default:
      return '⭐';
  }
};

export const MotivationHistoryScreen: React.FC = () => {
  const { t } = useTrans();
  const navigate = useNavigate();

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useEarnHistory();

  const history = useMemo(() => data?.pages.flat() ?? [], [data]);

  // Group history by date
  const groupedHistory = useMemo(() => {
    const groups: { [key: string]: EarnModelHistory[] } = {};
    history.forEach((item) => {
      const date = dayjs(item.processedAt).format('YYYY-MM-DD');
      if (!groups[date]) groups[date] = [];
      groups[date].push(item);
    });
    return groups;
  }, [history]);

  if (isLoading) {
    return <LoadingContainer text={t('motivation.loadingHistory', { defaultValue: 'Loading history...' })} />;
  }

  return (
    <ScreenContainer>
      <PageHeader>
        <HeaderContent>
          <BackButton onClick={() => navigate(-1)}>←</BackButton>
          <HeaderTitle>📜 {t('motivation.pointsHistory', { defaultValue: 'Points History' })}</HeaderTitle>
        </HeaderContent>
      </PageHeader>

      <PageContent>
        {history.length === 0 ? (
          <EmptyState>
            <EmptyEmoji>📜</EmptyEmoji>
            {t('motivation.noHistory', { defaultValue: 'No points history yet. Start earning points!' })}
          </EmptyState>
        ) : (
          <HistoryList>
            {Object.entries(groupedHistory).map(([date, items]) => (
              <React.Fragment key={date}>
                <DateHeader>
                  {dayjs(date).isToday()
                    ? t('motivation.today', { defaultValue: 'Today' })
                    : dayjs(date).isYesterday()
                      ? t('motivation.yesterday', { defaultValue: 'Yesterday' })
                      : dayjs(date).format('dddd, MMMM D')}
                </DateHeader>
                {items.map((item) => (
                  <HistoryItem key={item.id}>
                    <HistoryThumbnail src={item.media ? getMediaSmallest(item.media) : undefined}>
                      {!item.media && getIconForEventType(item.eventType)}
                    </HistoryThumbnail>
                    <HistoryContent>
                      <HistoryTitle>{toText(item.title) || toText(item.ruleIdentifier) || 'Points earned'}</HistoryTitle>
                      <HistoryMeta>
                        {item.typeName && <TypeBadge>{toText(item.typeName)}</TypeBadge>}
                        <span>{dayjs(item.processedAt).format('HH:mm')}</span>
                      </HistoryMeta>
                    </HistoryContent>
                    <HistoryPoints>+{item.pointsCollected}</HistoryPoints>
                  </HistoryItem>
                ))}
              </React.Fragment>
            ))}

            {hasNextPage && (
              <LoadMoreButton onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                {isFetchingNextPage
                  ? t('motivation.loading', { defaultValue: 'Loading...' })
                  : t('motivation.loadMore', { defaultValue: 'Load more' })}
              </LoadMoreButton>
            )}
          </HistoryList>
        )}
      </PageContent>
    </ScreenContainer>
  );
};
